import type { MetricResult, RecordBase } from './model';

export const promotionStatuses = [
  'planejado',
  'em_veiculacao',
  'pausado',
  'encerrado',
  'cancelado',
] as const;
export type PromotionStatus = (typeof promotionStatuses)[number];
export interface PromotionBaselineReference {
  scope: 'total_lifetime';
  collectedAt: string;
  capturedAt: string;
  snapshotId: string;
  linkedMediaId: string;
  accountId: string;
  connectionId: string;
}
export interface PromotionPlan extends RecordBase {
  campaignId: string;
  publicationId: string;
  contentId: string;
  name: string;
  objective: string;
  budgetCents: number;
  startDate: string;
  endDate: string;
  status: PromotionStatus;
  externalReference: string;
  evidence: string;
  publicationUrl: string;
  account: string;
  platform: string;
  baseline: PromotionBaselineReference | null;
}
export interface PromotionPlanRevision extends RecordBase {
  planId: string;
  planVersion: number;
  plan: PromotionPlan;
}
export interface PromotionReading extends RecordBase {
  planId: string;
  planVersion: number;
  revision: number;
  replacesId: string | null;
  source: string;
  evidence: string;
  externalReference: string;
  scope: 'pago_declarado' | 'nao_separado';
  periodStart: string;
  periodEnd: string;
  spentCents: number | null;
  profileVisits: number | null;
  followers: number | null;
  interactions: number | null;
  videoViews: number | null;
}
export interface PromotionSummary {
  plan: PromotionPlan;
  baseline: { reference: PromotionBaselineReference; metrics: MetricResult[] } | null;
  revisions: PromotionPlanRevision[];
  readings: PromotionReading[];
  latest: PromotionReading | null;
  costPerVisitCents: number | null;
}
export interface PromotionWorkspaceData {
  campaignId: string;
  publications: {
    id: string;
    contentId: string;
    account: string;
    platform: string;
    url: string;
    publishedAt: string;
  }[];
  tests: PromotionSummary[];
}

/** Accept an ungrouped BRL decimal; all persisted monetary amounts are integer cents. */
export function brlToCents(value: string): number | null {
  const input = value.trim();
  if (!input) return null;
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(input))
    throw new Error('Use um valor em reais com até duas casas decimais, sem separador de milhar.');
  const [whole, fraction = ''] = input.replace(',', '.').split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) throw new Error('Valor monetário fora do limite permitido.');
  return cents;
}
